import React, { useState, useEffect, useRef } from 'react';
import { useQuery, gql } from '@apollo/client';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Badge,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import DemoContactModal from './DemoContactModal';
import {
  Dashboard as DashboardIcon,
  Settings as SettingsIcon,
  Warning as WarningIcon,
  Analytics as AnalyticsIcon,
  Api as ApiIcon,
  Menu as MenuIcon,
  AccountCircle,
  Logout,
  Timeline,
  ChevronLeft,
  ChevronRight,
  HistoryEdu,
  ContactMail,
  CategoryOutlined,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { event } from '../analytics';
import { useNavigationTracking } from '../hooks/useAuditLog';

const ACTIVE_ALERTS_COUNT_QUERY = gql`
  query ActiveAlertsCount {
    dashboardStats {
      activeAlertsCount
    }
  }
`;

/** Isolated component so polling only re-renders the badge, not the whole Layout/page */
const SidebarAlertsBadge: React.FC<{ icon: React.ReactNode }> = ({ icon }) => {
  const { data } = useQuery(ACTIVE_ALERTS_COUNT_QUERY, {
    pollInterval: 120000,
  });
  const count = data?.dashboardStats?.activeAlertsCount || 0;
  return count > 0 ? (
    <Badge badgeContent={count} color="error">
      {icon}
    </Badge>
  ) : (
    <>{icon}</>
  );
};

const drawerWidthExpanded = 280;
const drawerWidthCollapsed = 72;

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  
  // Track navigation for audit logging
  useNavigationTracking();
  
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved === 'true';
  });
  const [demoWelcomeOpen, setDemoWelcomeOpen] = useState(false);
  const [demoReminderOpen, setDemoReminderOpen] = useState(false);
  const demoTimersRef = useRef<{ welcome?: ReturnType<typeof setTimeout>; reminder?: ReturnType<typeof setTimeout> }>({});

  const drawerWidth = sidebarCollapsed ? drawerWidthCollapsed : drawerWidthExpanded;

  const handleSidebarToggle = () => {
    const newState = !sidebarCollapsed;
    setSidebarCollapsed(newState);
    localStorage.setItem('sidebarCollapsed', String(newState));
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    event('logout', { event_category: 'auth', event_action: 'logout' });
    logout();
    handleClose();
    navigate('/login');
  };

  const handleCloseDemoWelcome = () => {
    localStorage.removeItem('verit_show_demo_welcome');
    localStorage.setItem('verit_demo_welcome_shown', 'true');
    setDemoWelcomeOpen(false);
  };

  const handleCloseDemoReminder = () => {
    localStorage.setItem('verit_demo_reminder_shown', 'true');
    setDemoReminderOpen(false);
  };

  // Demo modals: first after 5s, reminder after 10 min (once each)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const showWelcome = localStorage.getItem('verit_show_demo_welcome') === 'true';
    if (!showWelcome) return;

    const now = Date.now();
    let landAt = localStorage.getItem('verit_demo_land_at');
    if (!landAt) {
      landAt = String(now);
      localStorage.setItem('verit_demo_land_at', landAt);
    }
    const landTs = parseInt(landAt, 10);

    demoTimersRef.current.welcome = setTimeout(() => {
      if (localStorage.getItem('verit_demo_welcome_shown') === 'true') return;
      setDemoWelcomeOpen(true);
    }, 5 * 1000);

    const reminderDelay = 10 * 60 * 1000 - (Date.now() - landTs);
    if (reminderDelay > 0) {
      demoTimersRef.current.reminder = setTimeout(() => {
        if (localStorage.getItem('verit_demo_welcome_shown') !== 'true') return;
        if (localStorage.getItem('verit_demo_reminder_shown') === 'true') return;
        setDemoReminderOpen(true);
      }, reminderDelay);
    }

    return () => {
      if (demoTimersRef.current.welcome) clearTimeout(demoTimersRef.current.welcome);
      if (demoTimersRef.current.reminder) clearTimeout(demoTimersRef.current.reminder);
    };
  }, []);

  const isDemoUser = Boolean(user?.username?.startsWith?.('demo_viewer_'));

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    { text: 'Sequences', icon: <Timeline />, path: '/sequences' },
    { text: 'Sequence Executions', icon: <AnalyticsIcon />, path: '/results' },
    { text: 'API Evaluations', icon: <ApiIcon />, path: '/api-results' },
    ...(user?.role === 'admin'
      ? [{ text: 'Evaluation types', icon: <CategoryOutlined />, path: '/evaluation-types' }]
      : []),
    { text: 'Alerts', icon: <SidebarAlertsBadge icon={<WarningIcon />} />, path: '/alerts' },
    { text: 'Audit Log', icon: <HistoryEdu />, path: '/audit-log' },
    { text: 'Settings', icon: <SettingsIcon />, path: '/settings' },
    ...(isDemoUser
      ? [
          {
            text: 'Contact Sales',
            icon: <ContactMail />,
            path: '/contact-sales',
            highlight: true,
          },
        ]
      : []),
  ];

  const drawer = (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100%',
      background: 'transparent'
    }}>
      <Box sx={{ 
        p: sidebarCollapsed ? 2 : 3, 
        textAlign: 'center', 
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        transition: 'padding 0.3s',
        mb: 2,
        backgroundColor: 'transparent',
        background: 'linear-gradient(180deg, rgba(8, 145, 178, 0.15) 0%, transparent 100%)',
      }}>
        {!sidebarCollapsed && (
          <>
            <Typography variant="h5" component="div" sx={{ 
              fontWeight: 700, 
              color: '#ffffff',
              mb: 0.5,
              letterSpacing: '-0.01em',
            }}>
              Verit Compliance
            </Typography>
            <Typography variant="body2" sx={{ color: '#7dd3fc', fontSize: '0.75rem' }}>
              AI Safety Intelligence Platform
            </Typography>
          </>
        )}
        {sidebarCollapsed && (
          <Typography variant="h6" component="div" sx={{ 
            fontWeight: 700, 
            color: '#ffffff',
          }}>
            VC
          </Typography>
        )}
      </Box>
      <List sx={{ px: 2, py: 1, flexGrow: 1 }}>
        {menuItems.map((item) => {
        const isHighlight = (item as { highlight?: boolean }).highlight;
        return (
          <ListItem key={item.text} disablePadding sx={{ mb: 1.5 }}>
              <ListItemButton
              data-ga-label={`Nav: ${item.text}`}
              onClick={() => navigate(item.path)}
              selected={location.pathname === item.path}
              sx={{
                borderRadius: 2,
                justifyContent: sidebarCollapsed ? 'center' : 'initial',
                px: sidebarCollapsed ? 1.5 : 2.5,
                py: 1.25,
                transition: 'all 0.2s ease-in-out',
                color: '#ffffff',
                ...(isHighlight && {
                  background: 'linear-gradient(90deg, rgba(245, 158, 11, 0.35) 0%, rgba(251, 191, 36, 0.15) 100%)',
                  color: '#fef3c7',
                  '&:hover': {
                    background: 'linear-gradient(90deg, rgba(245, 158, 11, 0.5) 0%, rgba(251, 191, 36, 0.25) 100%)',
                    color: '#ffffff',
                    transform: 'translateX(4px)',
                  },
                  '&.Mui-selected': {
                    background: 'linear-gradient(90deg, rgba(245, 158, 11, 0.5) 0%, rgba(251, 191, 36, 0.3) 100%)',
                    color: '#fef08a',
                    boxShadow: 'inset 3px 0 0 #fbbf24',
                  },
                  '& .MuiListItemIcon-root': { color: '#fde68a' },
                  '& .MuiListItemText-root': { color: 'inherit' },
                }),
                ...(!isHighlight && {
                  '&:hover': {
                    backgroundColor: 'rgba(8, 145, 178, 0.2)',
                    color: '#ffffff',
                    transform: 'translateX(4px)',
                  },
                  '&.Mui-selected': {
                    background: 'linear-gradient(90deg, rgba(8, 145, 178, 0.35) 0%, rgba(8, 145, 178, 0.1) 100%)',
                    color: '#22d3ee',
                    boxShadow: 'inset 3px 0 0 #22d3ee',
                    '&:hover': {
                      background: 'linear-gradient(90deg, rgba(8, 145, 178, 0.4) 0%, rgba(8, 145, 178, 0.15) 100%)',
                      transform: 'translateX(4px)',
                    },
                    '& .MuiListItemIcon-root': { color: '#22d3ee' },
                    '& .MuiListItemText-root': { color: '#22d3ee' },
                  },
                  '& .MuiListItemIcon-root': { color: 'inherit' },
                  '& .MuiListItemText-root': { color: 'inherit' },
                }),
              }}
              title={sidebarCollapsed ? item.text : undefined}
            >
              <ListItemIcon sx={{ 
                minWidth: sidebarCollapsed ? 0 : 40,
                justifyContent: 'center'
              }}>
                {item.icon}
              </ListItemIcon>
              {!sidebarCollapsed && <ListItemText primary={item.text} sx={{ '& .MuiTypography-root': { fontWeight: isHighlight ? 600 : 500, color: 'inherit' } }} />}
            </ListItemButton>
          </ListItem>
        );
        })}
      </List>
      <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />
      <Box sx={{ p: 1.5 }}>
        <IconButton
          data-ga-label="Sidebar toggle"
          onClick={handleSidebarToggle}
          sx={{
            width: '100%',
            borderRadius: 2,
            color: 'rgba(255, 255, 255, 0.7)',
            '&:hover': {
              backgroundColor: 'rgba(8, 145, 178, 0.2)',
              color: '#ffffff',
            },
          }}
        >
          {sidebarCollapsed ? <ChevronRight /> : <ChevronLeft />}
        </IconButton>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <DemoContactModal
        open={demoWelcomeOpen}
        onClose={handleCloseDemoWelcome}
        variant="welcome"
      />
      <DemoContactModal
        open={demoReminderOpen}
        onClose={handleCloseDemoReminder}
        variant="reminder"
      />

      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          backgroundColor: 'white',
          color: 'text.primary',
          boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
          borderBottom: '1px solid rgba(0,0,0,0.05)',
          transition: 'width 0.3s ease-in-out, margin 0.3s ease-in-out',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, fontWeight: 600, color: '#1e3a5f' }}>
            {menuItems.find(item => item.path === location.pathname)?.text || 'Verit Compliance'}
          </Typography>

          <IconButton
            size="large"
            aria-label="account of current user"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={handleMenu}
            color="inherit"
            sx={{
              '&:hover': {
                backgroundColor: 'rgba(30, 58, 95, 0.08)',
              },
            }}
          >
            <Avatar sx={{ 
              width: 36, 
              height: 36, 
              backgroundColor: '#1e3a5f',
              fontWeight: 600,
              fontSize: '0.95rem',
            }}>
              {user?.username?.charAt(0).toUpperCase() || 'U'}
            </Avatar>
          </IconButton>
          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleClose}
          >
            <MenuItem onClick={handleClose}>
              <ListItemIcon>
                <AccountCircle fontSize="small" />
              </ListItemIcon>
              Profile
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout} data-ga-label="Logout">
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ 
          width: { md: drawerWidth }, 
          flexShrink: { md: 0 },
          transition: 'width 0.3s ease-in-out',
          backgroundColor: '#1e3a5f'
        }}
        aria-label="navigation"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidthExpanded,
              borderRight: 'none',
              boxShadow: '4px 0 24px rgba(30, 58, 95, 0.2)',
              background: 'linear-gradient(180deg, #1e3a5f 0%, #2d4a6f 50%, #1e3a5f 100%) !important',
              backgroundImage: 'linear-gradient(180deg, #1e3a5f 0%, #2d4a6f 50%, #1e3a5f 100%) !important',
              backgroundColor: '#1e3a5f !important'
            },
          }}
          PaperProps={{
            sx: {
              background: 'linear-gradient(180deg, #1e3a5f 0%, #2d4a6f 50%, #1e3a5f 100%)',
              backgroundColor: '#1e3a5f'
            }
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              transition: 'width 0.3s ease-in-out',
              overflowX: 'hidden',
              borderRight: 'none',
              boxShadow: '4px 0 24px rgba(30, 58, 95, 0.2)',
              background: 'linear-gradient(180deg, #1e3a5f 0%, #2d4a6f 50%, #1e3a5f 100%) !important',
              backgroundImage: 'linear-gradient(180deg, #1e3a5f 0%, #2d4a6f 50%, #1e3a5f 100%) !important',
              backgroundColor: '#1e3a5f !important'
            },
          }}
          PaperProps={{
            sx: {
              background: 'linear-gradient(180deg, #1e3a5f 0%, #2d4a6f 50%, #1e3a5f 100%)',
              backgroundColor: '#1e3a5f'
            }
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          mt: '64px',
          backgroundColor: 'background.default',
          minHeight: 'calc(100vh - 64px)',
          transition: 'width 0.3s ease-in-out, margin 0.3s ease-in-out',
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default Layout;
